import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { REQUEST } from 'lib/express.tokens';
import { Request } from 'express';
import get from 'lodash-es/get';

import { contains, queryParams, stripGoogleTranslateParams } from 'lib/tools';
import { ExxComError } from 'lib/classes/exxcom-error.class';

const scriptName = 'router.service';

@Injectable()
export class RouterService {
    private subscriptions: any[] = [];

    baseUrl: string = '';
    isAccount: boolean = false;
    isCart: boolean = false;
    isCheckout: boolean = false;
    isCheckoutBilling: boolean = false;
    isCheckoutConfirmation: boolean = false;
    isCheckoutShipping: boolean = false;
    isFirstRoute: boolean = true;
    url: any = {
        previous: '',
        current: '',
        component: '',
        componentPrev: '',
        params: {},
        parts: [],
    };

    constructor(
        @Optional() @Inject(REQUEST) protected req: Request,
        @Inject('environment') private environment: any,
        @Inject(PLATFORM_ID) private platformId: Object,
        public router: Router
    ) {}

    init() {
        try {
            this.initRouteReuseStrategy();
            this.setBaseUrl();
            this.initUrl();
        } catch (err) {
            console.error(...new ExxComError(677634, scriptName, err).stamp());
        }
    }

    private initRouteReuseStrategy() {
        try {
            /**
             * Route Reuse Strategy
             * Reuses blog component on navigation (handles updates via queryParams subscription).
             * Other routes destroy on navigation (original behavior).
             */
            const getLeafRoute = (route: ActivatedRouteSnapshot): ActivatedRouteSnapshot => {
                while (route.firstChild) route = route.firstChild;
                return route;
            };

            this.router.routeReuseStrategy.shouldReuseRoute = (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean => {
                const futureLeaf = getLeafRoute(future);
                const currLeaf = getLeafRoute(curr);

                return (
                    futureLeaf.routeConfig?.path === 'blog' && (currLeaf.routeConfig?.path === 'blog' || currLeaf.component === futureLeaf.component)
                );
            };
        } catch (err) {
            console.error(...new ExxComError(620938, scriptName, err).stamp());
        }
    }

    private initUrl() {
        try {
            // Initialize URL immediately for SSR
            if (isPlatformServer(this.platformId) && this.req) {
                // During SSR, use REQUEST token (official Angular approach)
                this.parseUrlData(this.req.url);
            } else if (this.router.url) {
                // Client-side or if router.url is available
                this.parseUrlData(this.router.url);
            }

            // Set up listener for future navigations
            this.setupNavigationListener();
        } catch (err) {
            console.error(...new ExxComError(520938, scriptName, err).stamp());
        }
    }

    private parseUrlData(url: string) {
        try {
            // Strip Google Translate query params (_x_tr_*) to ensure clean URL matching
            // This fixes issues where Google Translate proxy adds params that break CMS lookups
            const cleanUrl = stripGoogleTranslateParams(url);

            this.url.base = this.baseUrl;
            this.url.previous = this.url.current;
            this.url.current = cleanUrl;
            this.url.full = `${this.baseUrl}${cleanUrl}`;
            this.url.params = queryParams.get(cleanUrl);
            const urlParts = decodeURIComponent(cleanUrl || '')
                .slice(1)
                .split('?');
            this.url.componentPrev = this.url.component;
            this.url.component = urlParts[0];
            this.url.parts = urlParts[0].split('/').filter(Boolean);
            this.url.fullPath = this.url.full.split('?')[0];
            this.isFirstRoute = !this.url.previous;
            this.isAccount = get(this.url, 'parts[0]') == 'account';
            this.isCart = get(this.url, 'parts[1]') == 'cart';
            this.isCheckout = get(this.url, 'parts[0]') == 'checkout';
            this.isCheckoutShipping = get(this.url, 'parts[1]') == 'shipping';
            this.isCheckoutBilling = get(this.url, 'parts[1]') == 'billing';
            this.isCheckoutConfirmation = get(this.url, 'parts[1]') == 'confirmation';
        } catch (err) {
            console.error(...new ExxComError(590923, scriptName, err).stamp());
        }
    }

    private setupNavigationListener() {
        try {
            this.onRouteChange('parseUrl', (event: any) => {
                this.parseUrlData(event.url);
            });
        } catch (err) {
            console.error(...new ExxComError(520938, scriptName, err).stamp());
        }
    }

    private setBaseUrl() {
        try {
            this.baseUrl = `${this.environment.backendApiUrl}`;
        } catch (err) {
            console.error(...new ExxComError(729088, scriptName, err).stamp());
        }
    }

    isCategoryPage(paths: string[]) {
        try {
            return get(this.url, 'parts[1]') && this.url.parts.length == 2 && contains(paths, this.url.parts[1]);
        } catch (err) {
            console.error(...new ExxComError(902873, scriptName, err).stamp());
        }
    }

    onRouteChange(scriptName: string, callback: any) {
        try {
            if (this.isOn(scriptName)) {
                return;
            }
            this.subscriptions[scriptName] = this.router.events.subscribe((event: any) => {
                if (!(event instanceof NavigationEnd)) {
                    return;
                }
                const urlParts = event.url.split('/').filter(Boolean);
                callback(event, urlParts);
            });
        } catch (err) {
            console.error(...new ExxComError(620394, scriptName, err).stamp());
        }
    }

    offRouteChange(scriptName: string) {
        try {
            if (!this.isOn(scriptName)) {
                return;
            }
            const subscription = this.subscriptions[scriptName];
            subscription && subscription.unsubscribe();
            delete this.subscriptions[scriptName];
        } catch (err) {
            console.error(...new ExxComError(902834, scriptName, err).stamp());
        }
    }

    isOn(scriptName: string) {
        try {
            return !!this.subscriptions[scriptName];
        } catch (err) {
            console.error(...new ExxComError(520933, scriptName, err).stamp());
        }
    }

    navigate(path) {
        try {
            return this.router.navigate(path);
        } catch (err) {
            console.error(...new ExxComError(527933, scriptName, err).stamp());
        }
    }
}
